import { Button, Modal } from '@mairistem/composants';
import { isNewIdentifiant, request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { ErrorToastContent } from '@/components/Autre';
import { REGISSEUR } from '@/constants';
import { deleteRegisseur, getSupprimable } from '@/routes';
import type { RegieRegisseurType } from '@/types';
import FormRegisseur from './FormRegisseur';

type ModalRegisseurProps = {
  regisseur: RegieRegisseurType;
  onClose: () => void;
  onRemove: (regisseur: RegieRegisseurType) => void;
  onSave: (regisseur: { actif: boolean; mandat: string }) => void;
};

const ModalRegisseur = (props: ModalRegisseurProps) => {
  const { regisseur, onClose, onRemove, onSave, ...rest } = props;

  const { identifiant, nom, prenom, actif, mandat } = regisseur;

  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState({
    mandat: 0,
    actif: false,
  });
  const [deletable, setDeletable] = React.useState(isNewIdentifiant(identifiant));

  const handleChange = React.useCallback((name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleRemoveClick = React.useCallback(() => {
    if (isNewIdentifiant(identifiant)) {
      onRemove(regisseur);
    } else {
      setLoading(true);

      request
        .delete(deleteRegisseur(identifiant))
        .then(() => {
          onRemove(regisseur);
        })
        .catch(() => {
          toast(
            <ErrorToastContent description={"La suppression du régisseur n'a pas été effectuée"} />,
            {
              type: 'error',
            },
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [identifiant, onRemove, regisseur]);

  React.useEffect(() => {
    setForm({
      mandat: _.findIndex(REGISSEUR.TYPES, (t) => t === mandat),
      actif,
    });
  }, [mandat, actif]);

  // Récupère l'info s'il est supprimable si c'est un régisseur existant
  React.useEffect(() => {
    if (!isNewIdentifiant(identifiant)) {
      setLoading(true);

      request
        .get(getSupprimable(identifiant))
        .then(({ supprimable }: { supprimable: boolean }) => {
          setDeletable(supprimable);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [identifiant]);

  const typeRegisseur = React.useMemo(() => {
    if (mandat === REGISSEUR.TYPE_SUPPLEANT || mandat === REGISSEUR.TYPE_AGENT) {
      return 'mandataire';
    }
    return 'régisseur';
  }, [mandat]);

  return (
    <Modal closeIcon open size="tiny" {...(!loading && { onClose })} {...rest}>
      <Modal.Header>{`Modification du ${typeRegisseur} ${_.trim(`${nom || ''} ${prenom || ''}`)}`}</Modal.Header>
      <Modal.Content>
        <FormRegisseur
          loading={loading}
          actif={form.actif}
          mandat={form.mandat}
          onChange={handleChange}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          data-testid="button-close"
          onClick={() => onClose()}
          basic
          size="tiny"
          icon="close"
          content="Annuler"
          loading={loading}
          disabled={loading}
        />
        {deletable && (
          <Button
            data-testid="button-delete"
            negative
            onClick={handleRemoveClick}
            content={`Supprimer le ${typeRegisseur}`}
            icon="checkmark"
            loading={loading}
            disabled={loading}
          />
        )}
        <Button
          data-testid="button-validate"
          positive
          onClick={() =>
            onSave({
              ...form,
              mandat: REGISSEUR.TYPES[form.mandat],
            })
          }
          content="Valider"
          icon="checkmark"
          loading={loading}
          disabled={loading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalRegisseur;
